import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    TextField,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    LinearProgress,
    Paper,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slide, // Import Slide for transition
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import baseUrl from '../config';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BookIcon from '@mui/icons-material/Book';
import ListItemIcon from '@mui/material/ListItemIcon';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close'; // Import CloseIcon for the dialog

import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Transition component for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Styled Components for visual enhancements
const HomeworkCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    boxShadow: '0 3px 7px rgba(0,0,0,0.07)',
    '&:hover': {
        boxShadow: '0 5px 10px rgba(0,0,0,0.1)',
        transform: 'translateY(-3px)',
    },
    transition: 'box-shadow 0.3s ease, transform 0.3s ease',
    borderColor: '#e0e0e0',
    borderWidth: '1px',
    borderStyle: 'solid',
}));

const CardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#f3f7fa',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
}));

const CardContentArea = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(3),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 3.5),
    borderRadius: 10,
    fontWeight: 600,
    backgroundColor: '#43a047',
    color: 'white',
    '&:hover': {
        backgroundColor: '#388e3c',
        boxShadow: theme.shadows[3],
    },
}));

const FileInput = styled('input')({
    display: 'none',
});

const UploadButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(1, 0, 2, 0),
    backgroundColor: '#64b5f6',
    color: 'white',
    padding: theme.spacing(1, 2.5),
    borderRadius: 8,
    '&:hover': {
        backgroundColor: '#42a5f5',
        boxShadow: theme.shadows[2],
    },
}));

const NoHomeworkPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: theme.shadows[2],
    backgroundColor: '#fafafa',
    textAlign: 'center',
}));

// Styled Dialog components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': { // Targets the paper component of the dialog
        borderRadius: 15,
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)', // More pronounced shadow
        border: '1px solid #e0e0e0', // Subtle border
        background: 'linear-gradient(145deg, #f0f4f8, #e6e9ee)', // Light gradient background
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: '#1976D2', // Primary blue header
    color: 'white',
    padding: theme.spacing(2, 3),
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.25rem',
    fontWeight: 600,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    fontSize: '1rem',
    lineHeight: 1.6,
    color: '#333', // Darker text for readability
    maxHeight: '70vh', // Limit content height to enable scrolling
    overflowY: 'auto', // Enable vertical scrolling
    '&::-webkit-scrollbar': { // Custom scrollbar for webkit browsers
        width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#bdbdbd',
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1',
    },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: 'flex-end', // Align close button to the right
}));

const StudentHomeworkList = () => {
    const navigate = useNavigate();
    const [homework, setHomework] = useState([]);
    const [submission, setSubmission] = useState({ homeworkId: '', comments: '', file: null, fileName: '' });
    const [uploadProgress, setUploadProgress] = useState(0);

    // State for the description dialog
    const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
    const [currentDescription, setCurrentDescription] = useState('');
    const [currentAssignmentTitle, setCurrentAssignmentTitle] = useState('');

    useEffect(() => {
        const fetchHomework = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${baseUrl}api/students/student/homework`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setHomework(response.data.homework);
            } catch (error) {
                console.error('Error fetching homework:', error);
            }
        };
        fetchHomework();
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        console.log(selectedFile);
        setSubmission({ ...submission, file: selectedFile, fileName: selectedFile ? selectedFile.name : '' });
        setUploadProgress(0);
    };

    const handleSubmitHomework = async () => {
        const formData = new FormData();
        formData.append('homeworkId', submission.homeworkId);
        formData.append('comments', submission.comments);
        formData.append('file', submission.file);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${baseUrl}api/students/student/homework/submit`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                },
            });
            alert('Homework submitted successfully!');
            // Update the specific homework item in the state
            setHomework(prevHomework =>
                prevHomework.map(hw =>
                    hw._id === submission.homeworkId
                        ? { ...hw, submission: response.data.submission }
                        : hw
                )
            );
            setSubmission({ homeworkId: '', comments: '', file: null, fileName: '' });
            setUploadProgress(0);
        } catch (error) {
            console.error('Error submitting homework:', error);
            setUploadProgress(0);
        }
    };

    const handleOpenDescriptionDialog = (description, title) => {
        setCurrentDescription(description);
        setCurrentAssignmentTitle(title);
        setOpenDescriptionDialog(true);
    };

    const handleCloseDescriptionDialog = () => {
        setOpenDescriptionDialog(false);
        setCurrentDescription('');
        setCurrentAssignmentTitle('');
    };

    return (
        <Box sx={{ position: "relative", p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton sx={{ mr: 2 }} onClick={() => navigate(-1)}>
                    <ArrowBackIcon sx={{ fontSize: '1.5rem', color: '#1976D2' }} />
                </IconButton>
                <Box>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold', color: '#333',
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: "uppercase",
                        letterSpacing: {
                            xs: "0.05em",
                            md: "0.1em",
                        },
                        background: "linear-gradient(45deg, #111, #FF9800)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        display: "inline-block",
                        textAlign: "center",
                    }}>
                        My Assignments
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ marginTop: 1, fontSize: '1.1rem' }}>
                        Future Learning Pathways
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
            </Box>

            {homework.length === 0 ? (
                <NoHomeworkPaper>
                    <BookIcon color="primary" style={{ fontSize: 60, marginBottom: '20px' }} />
                    <Typography variant="h6" style={{ fontWeight: 500, color: '#555' }}>
                        No homework assigned yet.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
                        Check back later for new assignments.
                    </Typography>
                </NoHomeworkPaper>
            ) : (
                <Grid container spacing={3} style={{ marginTop: '20px' }}>
                    {homework.map((hw) => (
                        <Grid item xs={12} md={6} lg={4} key={hw._id}>
                            <HomeworkCard>
                                <CardHeader>
                                    <Typography variant="h6" component="div" style={{ display: 'flex', alignItems: 'center', color: '#1a237e', fontWeight: 500 }}>
                                        <AssignmentIcon style={{ marginRight: '8px', fontSize: '28px' }} color="primary" /> {hw.title}
                                    </Typography>
                                </CardHeader>
                                <CardContentArea>
                                    <List dense>
                                        <ListItem disableGutters>
                                            <ListItemIcon><EventIcon color="action" /></ListItemIcon>
                                            <ListItemText primary={`Due Date: ${new Date(hw.dueDate).toLocaleDateString()}`} />
                                        </ListItem>
                                    </List>

                                    {hw.description && (
                                        <Box mt={2}>
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                📖 Assignment Details
                                            </Typography>
                                            {hw.description.length > 200 ? (
                                                <>
                                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {`${hw.description.substring(0, 200)}...`}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        onClick={() => handleOpenDescriptionDialog(hw.description, hw.title)}
                                                        endIcon={<VisibilityIcon />}
                                                        sx={{ mt: 1, color: '#1976D2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } }}
                                                    >
                                                        Read More
                                                    </Button>
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {hw.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {hw.submission ? (
                                        <>
                                            <Box mt={2}>
                                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                    📝 Submitted Homework
                                                </Typography>

                                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                    <InsertDriveFileIcon color="secondary" />
                                                    <a
href={`${baseUrl.replace(/\/$/, '')}/${hw.submission.fileUrl.replace(/^\//, '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1976D2', textDecoration: 'none', fontWeight: 500 }}
                                                    >
                                                        {hw.submission.fileUrl.split('/').pop()}
                                                    </a>
                                                </Box>

                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    <strong>Comments:</strong> {hw.submission.comments || 'No comments provided'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Submitted At:</strong> {new Date(hw.submission.submittedAt).toLocaleString()}
                                                </Typography>
                                            </Box>

                                            <Box mt={3} p={2} sx={{ backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>
                                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                    📊 Evaluation
                                                </Typography>

                                                {hw.status === 'graded' && hw.grade ? (
                                                    <>
                                                        <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                            Grade: {hw.grade.score}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Feedback:</strong> {hw.grade.feedback || 'No feedback provided'}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="warning.main" fontStyle="italic">
                                                        Awaiting teacher's review and grading.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <TextField
                                                label="Comments"
                                                multiline
                                                rows={2}
                                                placeholder="Add your comments or questions here"
                                                variant="outlined"
                                                fullWidth
                                                margin="normal"
                                                value={hw._id === submission.homeworkId ? submission.comments : ''}
                                                onChange={(e) =>
                                                    setSubmission({ ...submission, homeworkId: hw._id, comments: e.target.value })
                                                }
                                            />

                                            <Box mt={2} display="flex" flexDirection="column" alignItems="flex-start">
                                                <label htmlFor={`file-upload-${hw._id}`}>
                                                    <FileInput
                                                        id={`file-upload-${hw._id}`}
                                                        type="file"
                                                        onChange={handleFileChange}
                                                    />
                                                    <UploadButton
                                                        component="span"
                                                        variant="contained"
                                                        startIcon={<CloudUploadIcon />}
                                                    >
                                                        Upload File
                                                    </UploadButton>
                                                </label>

                                                {submission.fileName && (
                                                    <Box display="flex" alignItems="center" mt={1}>
                                                        <InsertDriveFileIcon color="secondary" style={{ marginRight: '5px' }} />
                                                        <Typography variant="body2" style={{ color: '#444' }}>
                                                            {submission.fileName}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {uploadProgress > 0 && uploadProgress < 100 && (
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={uploadProgress}
                                                        sx={{ width: '100%', mt: 1 }}
                                                    />
                                                )}

                                                <SubmitButton
                                                    variant="contained"
                                                    endIcon={<SendIcon />}
                                                    onClick={handleSubmitHomework}
                                                    disabled={hw._id !== submission.homeworkId}
                                                >
                                                    Submit Homework
                                                </SubmitButton>
                                            </Box>
                                        </>
                                    )}
                                </CardContentArea>
                            </HomeworkCard>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Styled Description Dialog */}
            <StyledDialog
                open={openDescriptionDialog}
                onClose={handleCloseDescriptionDialog}
                maxWidth="md"
                fullWidth
                TransitionComponent={Transition} // Apply transition
            >
                <StyledDialogTitle>
                    <span>{currentAssignmentTitle} - Assignment Details</span>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDescriptionDialog}
                        sx={{
                            color: 'white', // White close icon
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)', // Subtle hover effect
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </StyledDialogTitle>
                <StyledDialogContent dividers>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {currentDescription}
                    </Typography>
                </StyledDialogContent>
                <StyledDialogActions>
                    <Button
                        onClick={handleCloseDescriptionDialog}
                        variant="contained"
                        sx={{
                            backgroundColor: '#FF5722', // Orange for Close button
                            '&:hover': {
                                backgroundColor: '#E64A19',
                            },
                        }}
                    >
                        Close
                    </Button>
                </StyledDialogActions>
            </StyledDialog>
        </Box>
    );
};

export default StudentHomeworkList;